/**
 * §9.3 — the draft triage queue.
 *
 * The sync writes a header and the bank's amount and stops: no detail lines,
 * no accounts, nothing that says what the money was for. This screen is where
 * that backlog gets looked at.
 *
 * NO PRODUCTION COUNT LIVES IN THIS COMMENT, and that is the rule now rather
 * than an omission. Run `apps/api/scripts/state.mjs`; CLAUDE.md's "Numbers in
 * documentation" section says why.
 *
 * This file is where that rule was learned. The comment here used to open
 * "Production holds 314 Mercury headers, every one a draft, none with a single
 * detail line ... nothing has ever been coded and nothing has ever been
 * posted." Every clause of it was false for days before anyone noticed,
 * because a count with no date reads as a fact about today forever.
 *
 * THE KEYBOARD MODEL COMES FROM prototype/FINDINGS.md, THE CODE DOES NOT
 *
 * `j`/`k` move, `space` selects, `d` dismisses. That model was validated in
 * Phase 0.5 against 28 fixture transactions and is the part of the prototype
 * worth keeping. The prototype itself is throwaway by its own README — no API,
 * no auth, hardcoded fixtures — so nothing here is lifted from it.
 *
 * WHAT IS DELIBERATELY MISSING: `a`, ACCEPT SUGGESTION
 *
 * The prototype binds `a` to accepting a confident suggestion. §9.7's
 * suggestion engine does not exist — there is no endpoint, nothing scores an
 * account, and `header_embeddings` has no writer. So `a` is unbound rather
 * than wired to something invented, and no row shows an accept affordance.
 *
 * That happens to be what FINDINGS.md asks for anyway: a below-threshold
 * suggestion must not render the same affordance as a confident one, because
 * accept-by-reflex is the thing that would make the whole feature worthless.
 * The weakest version of that rule is the one in force here — no affordance at
 * all, because there is nothing behind it.
 *
 * WHY THE DETAIL PANE IS READ-ONLY
 *
 * Coding an entry needs the editor, which is the next slice. What this pane
 * does is show the bank's facts — amount, counterparty, category, the Mercury
 * transaction id — so an operator can decide whether a transaction is worth
 * coding or is noise to dismiss. That decision is the whole job of a triage
 * screen, and it does not require editing.
 */

import { useCan } from '../auth/AuthProvider.js';
import { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { formatCents } from '../lib/money.js';
import { kindLabel, statusLabel } from '../lib/labels.js';
import { ConfirmPost } from '../components/ConfirmPost.js';
import {
  describeAttached,
  describeRefused,
  ReceiptDropzone,
  reasonFor,
  uploadReceipt,
} from '../components/ReceiptDropzone.js';
import { Toast } from '../components/Toast.js';
import { ReceiptOverlay } from '../components/ReceiptOverlay.js';
import { TriStateCheck, type TriState } from '../components/TriStateCheck.js';
import {
  ApiError,
  api,
  type Attachment,
  type JournalEntry,
  type JournalFilters,
  type JournalList,
  type JournalListRow,
  type JournalStatus,
  type BulkPlan,
  type BulkPlanEntry,
  type BulkResult,
  type BulkSkipped,
  type BulkSkipReason,
} from '../api.js';

/** A bank amount is signed: negative is money leaving. */
function bankAmountLabel(cents: number | null): string {
  if (cents === null) return '—';
  return formatCents(cents);
}

/**
 * What to show in the queue's Amount column.
 *
 * A bank row has a signed bank amount and that is the number: negative is
 * money leaving. A MANUAL row has none -- no cash moved when an invoice was
 * issued -- so the column was empty for all thirteen §17 invoices, on the one
 * screen where you go looking for them.
 *
 * The debit total stands in. It is UNSIGNED on purpose and that is the honest
 * limitation: for a bank row the sign means a direction of cash, and a manual
 * entry has no cash direction to report. An invoice and an accrued expense are
 * both "$7,171.20 of entry" here, and which one it is comes from the accounts,
 * which is where it actually lives.
 *
 * Still an em dash when there is nothing to show -- a manual header created
 * and not yet coded has no bank amount AND no lines.
 */
function amountLabel(row: JournalListRow): string {
  if (row.bankAmountCents !== null) return formatCents(row.bankAmountCents);
  if (row.lineCount > 0) return formatCents(row.debitTotalCents);
  return '—';
}

/**
 * The operator-facing name for a row's state.
 *
 * Deliberately the same four words as the Status filter. A detail pane reading
 * "draft" beside a filter offering "Uncategorized" made them look like
 * different things, and lower-case schema values leaking into the interface is
 * how that happens.
 */
/**
 * What the operator is looking for, which is NOT what `status` stores.
 *
 * `draft` covers two things they think of as unrelated: 312 bank transactions
 * nobody has coded, and a handful of entries that are fully coded and waiting
 * to be posted. Calling both "Draft" made the second invisible -- the eleven
 * seeded invoices could only be found by searching a counterparty name.
 *
 * So the control asks the question the operator is actually asking, and this
 * maps it onto the two API parameters that answer it.
 */
type ViewKey = 'uncategorized' | 'draft' | 'posted' | 'excluded' | 'all';

/**
 * The four states an operator recognises, plus All.
 *
 * `status` in the database is three values and covers two of these: a `draft`
 * with no lines is a bank transaction nobody has categorised, and a `draft`
 * with lines is an entry waiting to be posted. They are different jobs and the
 * schema does not separate them, so this does.
 *
 * "Excluded" rather than "Dismissed": a personal charge on the business card is
 * excluded from the books, not merely set aside. The column stays
 * `dismissed_at` -- renaming it would be a migration that buys nothing and
 * breaks the audit trail's continuity -- but nothing operator-facing says
 * "dismiss" any more.
 */
const VIEWS: Record<
  ViewKey,
  { label: string; status?: 'draft' | 'posted' | 'dismissed'; uncoded?: boolean }
> = {
  uncategorized: { label: 'Uncategorized', status: 'draft', uncoded: true },
  draft: { label: 'Draft', status: 'draft', uncoded: false },
  posted: { label: 'Posted', status: 'posted' },
  excluded: { label: 'Excluded', status: 'dismissed' },
  // No status and no coding filter: everything the tenant has.
  all: { label: 'All' },
};

/**
 * Where a row came from -- the difference between "the bank told us this
 * happened" and "I am asserting this happened", which is what §17's invoices
 * exist on.
 */
// `bank` is every row a bank reported: Mercury's and an imported file's.
type SourceKey = 'bank' | 'manual' | 'all';

const SOURCES: Record<SourceKey, string> = {
  bank: 'Bank Feed',
  manual: 'Manual Entry',
  all: 'All',
};

/**
 * §16.2 — why a batch left entries alone, in the operator's words.
 *
 * Every skip carries a reason and every reason is actionable, so this says
 * which rather than "some were skipped". `no_rule` is the one that matters
 * most: it is not a fault, it is the batch declining to guess, and it names
 * the work left to do.
 */
function describeSkips(skipped: BulkSkipped[]): string {
  if (skipped.length === 0) return 'Nothing to categorize.';
  const counts = new Map<BulkSkipReason, number>();
  for (const row of skipped) counts.set(row.reason, (counts.get(row.reason) ?? 0) + 1);

  const words: Record<BulkSkipReason, string> = {
    no_rule: 'no rule matches',
    already_coded: 'already being categorized',
    not_a_draft: 'not a draft',
    no_bank_amount: 'no bank amount',
    no_mapped_account: 'the account is not mapped',
    not_found: 'not found',
  };
  return `Nothing to categorize: ${[...counts]
    .map(([reason, n]) => `${n} where ${words[reason]}`)
    .join(', ')}.`;
}

/** What a finished batch did, with the failures named rather than counted. */
/** §9.1 — an entry whose other side is already posted: coding it would record the movement twice. */
function isDuplicate(entry: { counterpart: { status: string } | null }): boolean {
  return entry.counterpart?.status === 'posted';
}

function describeResult(result: BulkResult): string {
  const posted = result.applied.filter((row) => row.status === 'posted').length;
  const coded = result.applied.filter((row) => row.status === 'coded').length;
  const failed = result.applied.filter((row) => row.status === 'failed').length;
  const excluded = result.applied.filter((row) => row.status === 'excluded').length;

  const parts: string[] = [];
  if (posted > 0) parts.push(`${posted} posted`);
  if (coded > 0) parts.push(`${coded} categorized`);
  if (excluded > 0) parts.push(`${excluded} excluded as transfers`);
  if (failed > 0) parts.push(`${failed} refused`);
  if (result.skipped.length > 0) parts.push(`${result.skipped.length} skipped`);
  return parts.length > 0 ? parts.join(' · ') : 'Nothing changed.';
}

const PAGE_SIZE = 50;

export function TriagePage() {
  // What this screen offers (0069); the API refuses the rest either way.
  const edits = useCan()('edit_entries');
  const navigate = useNavigate();
  /*
   * THE VIEW LIVES IN THE URL, not in component state.
   *
   * Two things needed it there. The editor offers Previous and Next through
   * the queue, and it can only reproduce the list the operator was looking at
   * if the filter travels with the link. And a queue tab that survives a
   * refresh, a back button and a pasted link is worth having on its own.
   */
  const [searchParams, setSearchParams] = useSearchParams();
  const view: ViewKey = (() => {
    const raw = searchParams.get('view');
    return raw !== null && raw in VIEWS ? (raw as ViewKey) : 'uncategorized';
  })();
  const setView = (next: ViewKey) => {
    // `replace`, so walking the tabs does not build a back-button trail of
    // every filter the operator tried on the way to the one they wanted.
    setSearchParams(next === 'uncategorized' ? {} : { view: next }, { replace: true });
  };
  const [source, setSource] = useState<SourceKey>('all');
  /** The shortcut list, collapsed by default -- see the footer. */
  const [keysOpen, setKeysOpen] = useState(false);
  const [search, setSearch] = useState('');
  /*
   * Advanced search, 2026-09-13. Both are SERVER filters, not a filter over
   * the rows on screen: the Auto-Filled badge is drawn per page, and filtering
   * fifty rows would say "none" while the next page held a hundred. The API's
   * condition is the bulk plan's eligibility, so the filter and Auto Post agree.
   */
  /*
   * Three-valued since 2026-09-15, at the operator's request: 'no' is the
   * state that finds the rows with no badge -- nothing will code this, or
   * nothing is attached to it -- which a checkbox could not ask for. See
   * TriStateCheck for why it is not a native checkbox.
   */
  const [advancedOpen, setAdvancedOpen] = useState(false);
  const [autoFilledFilter, setAutoFilledFilter] = useState<TriState>('any');
  const [receiptFilter, setReceiptFilter] = useState<TriState>('any');
  /** The query-string value for a filter, or undefined when it is off. */
  const triParam = (state: TriState) =>
    state === 'any' ? undefined : state === 'yes' ? ('true' as const) : ('false' as const);
  const activeAdvanced =
    (autoFilledFilter === 'any' ? 0 : 1) + (receiptFilter === 'any' ? 0 : 1);
  /** The bulk menu under the caret, and the Exclude confirmation it opens. */
  const [bulkMenuOpen, setBulkMenuOpen] = useState(false);
  const [confirmingExclude, setConfirmingExclude] = useState(false);
  const bulkMenuRoot = useRef<HTMLDivElement>(null);
  /** The confirmation's "exclude the other side of these transfers" box. */
  const [excludeTransfers, setExcludeTransfers] = useState(false);
  /** The confirmation's "exclude it instead of coding it" box, for duplicates. */
  const [excludeDuplicates, setExcludeDuplicates] = useState(false);
  /** Memos typed in the confirmation, by header id. Untouched entries are absent. */
  const [memoEdits, setMemoEdits] = useState<Record<string, string>>({});
  /** The row a file is being dragged over, and the row whose upload is running. */
  const [dropRow, setDropRow] = useState<string | null>(null);
  const [uploadingRow, setUploadingRow] = useState<string | null>(null);

  /**
   * What the editor needs to reproduce this list: the view, and the search
   * the operator had typed. Nothing else -- paging is deliberately absent, and
   * the editor says what that costs.
   */
  const editorSuffix = (() => {
    const params = new URLSearchParams();
    if (view !== 'uncategorized') params.set('view', view);
    if (search.trim() !== '') params.set('q', search.trim());
    const autoParam = triParam(autoFilledFilter);
    if (autoParam) params.set('autoFilled', autoParam);
    const receiptParam = triParam(receiptFilter);
    if (receiptParam) params.set('hasReceipt', receiptParam);
    const query = params.toString();
    return query ? `?${query}` : '';
  })();
  const [offset, setOffset] = useState(0);

  const [list, setList] = useState<JournalList | null>(null);
  const [listError, setListError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);

  const [cursor, setCursor] = useState(0);
  const [selected, setSelected] = useState<Set<string>>(new Set());

  /*
   * §16.2 — the batch, from plan to confirmation to report.
   *
   * `plan` is what the server says it would write; nothing has happened while
   * it is on screen. `post` is remembered alongside it because the dialog is
   * the same for both and the answer has to know which it was confirming.
   */
  const [plan, setPlan] = useState<BulkPlan | null>(null);
  /**
   * Entries the operator unticked in the confirmation, so the batch can be
   * narrowed without cancelling and re-selecting. Ids rather than a filtered
   * list: the dialog goes on drawing them so they can be put back.
   */
  const [droppedFromPlan, setDroppedFromPlan] = useState<string[]>([]);
  const [planPosts, setPlanPosts] = useState(false);
  const [bulkBusy, setBulkBusy] = useState(false);

  /*
   * §9.7 — what the rules WOULD code, for the rows currently on screen.
   *
   * The same `dryRun` the bulk confirmation uses, drawn once per page instead
   * of once per press. Two things read it: the Auto-Filled badge on a row, and
   * the recommended lines in the detail pane. Both used to require pressing a
   * button to find out.
   *
   * ONE REQUEST, NOT ONE PER ROW, and not a second definition of which rule
   * wins. `suggestFromRules` orders by priority, then created_at, then id, and
   * a copy of that ordering written into a list query for speed is exactly the
   * duplication `bulk-coding.ts` refused to make. The plan already answers the
   * question; this asks it earlier.
   *
   * Keyed by header id so a row can be looked up in constant time while the
   * list renders.
   */
  const [offers, setOffers] = useState<Map<string, BulkPlanEntry>>(new Map());

  async function openBulk(post: boolean) {
    setBulkBusy(true);
    setListError(null);
    try {
      const drawn = await api.planBulkCoding([...selected]);
      if (drawn.entries.length === 0) {
        // Nothing to confirm. Saying why beats an empty dialog: every one of
        // these has a reason attached and the operator can act on it.
        setNotice(describeSkips(drawn.skipped));
        return;
      }
      setPlanPosts(post);
      setExcludeTransfers(false);
      setExcludeDuplicates(false);
      // Every entry starts ticked. Cleared here rather than on close, so a
      // dialog reopened after a cancel is not still carrying the last one's
      // exclusions against a batch that may not even contain those entries.
      setDroppedFromPlan([]);
      setMemoEdits({});
      setPlan(drawn);
    } catch (caught) {
      setListError(
        caught instanceof ApiError ? caught.message : 'Could not work out what to categorize.',
      );
    } finally {
      setBulkBusy(false);
    }
  }

  async function runBulk() {
    if (!plan) return;
    setBulkBusy(true);
    // Only a memo that actually changed is sent. Trimmed, and an emptied one is
    // null, which is how the editor saves the same field.
    const memos: Record<string, string | null> = {};
    /*
     * The batch is what is still ticked, and it is derived once here so the
     * ids sent and the memos sent cannot disagree. A memo typed against an
     * entry that was then unticked is not sent: the entry is not being
     * written at all, and sending the memo would edit an entry the operator
     * had just taken out of the batch.
     */
    const dropped = new Set(droppedFromPlan);
    const batch = plan.entries.filter((entry) => !dropped.has(entry.headerId));
    if (batch.length === 0) return;
    for (const entry of batch) {
      if (!Object.hasOwn(memoEdits, entry.headerId)) continue;
      const typed = memoEdits[entry.headerId]!.trim();
      const next = typed === '' ? null : typed;
      if (next !== entry.memo) memos[entry.headerId] = next;
    }
    try {
      const result = await api.applyBulkCoding(
        batch.map((entry) => entry.headerId),
        planPosts,
        {
          excludeCounterparts: excludeTransfers,
          excludeDuplicates,
          ...(Object.keys(memos).length > 0 && { memos }),
        },
      );
      setPlan(null);
      setSelected(new Set());
      setNotice(describeResult(result));
      await load();
    } catch (caught) {
      setListError(caught instanceof ApiError ? caught.message : 'The batch did not run.');
    } finally {
      setBulkBusy(false);
    }
  }

  /*
   * Exclude, for every selected entry. The single-entry exclude, repeated: there
   * is no bulk endpoint, and dismissing is conditional on the row still being a
   * draft, so each refusal is its own answer. What was refused is counted and
   * the first reason is said; everything else went through.
   */
  async function excludeSelected() {
    setBulkBusy(true);
    setListError(null);
    let excluded = 0;
    const refusals: string[] = [];
    for (const headerId of [...selected]) {
      try {
        await api.dismissJournal(headerId);
        excluded += 1;
      } catch (caught) {
        refusals.push(caught instanceof ApiError ? caught.message : 'Could not exclude an entry.');
      }
    }
    setSelected(new Set());
    setConfirmingExclude(false);
    setBulkBusy(false);
    const done = `Excluded ${excluded} ${excluded === 1 ? 'entry' : 'entries'}`;
    setNotice(
      refusals.length === 0 ? done : `${done}. ${refusals.length} left alone: ${refusals[0]}`,
    );
    load();
  }

  /*
   * A receipt dropped straight onto a row, 2026-09-13 -- one gesture instead of
   * selecting the row and then dropping on the pane. The same upload the drop
   * zone runs (uploadReceipt), to THAT row's entry, whichever row the cursor is
   * on. The list is re-read so the row's receipt badge appears.
   */
  async function dropReceipt(target: JournalListRow, files: File[]) {
    setUploadingRow(target.id);
    // Every dropped file, one after another; a refused one is named and the
    // rest still attach -- the same rule as the drop zone (2026-09-14).
    const attached: Awaited<ReturnType<typeof uploadReceipt>>[] = [];
    const refused: Array<{ name: string; reason: string }> = [];
    try {
      for (const file of files) {
        try {
          attached.push(await uploadReceipt(target.id, file));
        } catch (caught) {
          refused.push({ name: file.name, reason: reasonFor(caught) });
        }
      }
    } finally {
      setUploadingRow(null);
    }

    const said: string[] = [];
    if (attached.length > 0) {
      said.push(`Attached ${describeAttached(attached)} to ${target.counterparty ?? 'the entry'}`);
      if (target.id === activeIdRef.current) {
        setReceipts((current) => [...current, ...attached]);
      }
      load();
    }
    if (refused.length > 0) said.push(describeRefused(refused, files.length));
    setNotice(said.join('. '));
  }

  /* The bulk menu closes on a click elsewhere and on Escape, as NewEntryMenu does. */
  useEffect(() => {
    if (!bulkMenuOpen) return;
    function onPointer(event: MouseEvent) {
      if (!bulkMenuRoot.current?.contains(event.target as Node)) setBulkMenuOpen(false);
    }
    function onKey(event: KeyboardEvent) {
      if (event.key === 'Escape') setBulkMenuOpen(false);
    }
    document.addEventListener('mousedown', onPointer);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onPointer);
      document.removeEventListener('keydown', onKey);
    };
  }, [bulkMenuOpen]);

  const [entry, setEntry] = useState<JournalEntry | null>(null);
  const [entryError, setEntryError] = useState<string | null>(null);

  /*
   * §7 — the receipts on the entry under the cursor.
   *
   * Held apart from `entry` because GET /journals/:id does not carry them and
   * a failure to list them must not blank the pane. A transaction with no
   * receipt is the ordinary case here; a transaction whose receipt could not
   * be listed is a different thing and says so.
   */
  const [receipts, setReceipts] = useState<Attachment[]>([]);
  const [receiptsError, setReceiptsError] = useState<string | null>(null);
  /*
   * Which row the cursor is on RIGHT NOW, for a callback that outlives the
   * render that made it.
   *
   * An upload begun on one row can finish after the operator has arrowed onto
   * another, and the handler that started it still holds the old row. Adding
   * its receipt to the list then puts an attachment under the wrong entry --
   * the same failure the load effect clears `receipts` to avoid, arriving by a
   * different route. Compared against the id the upload actually went to.
   */
  const activeIdRef = useRef<string | undefined>(undefined);
  /** The one being looked at in the overlay, or null. */
  const [viewing, setViewing] = useState<Attachment | null>(null);

  const [notice, setNotice] = useState<string | null>(null);

  const rowsRef = useRef<Array<HTMLLIElement | null>>([]);

  const filters: JournalFilters = {
    ...(VIEWS[view].status !== undefined && { status: VIEWS[view].status }),
    ...(VIEWS[view].uncoded !== undefined && {
      uncoded: VIEWS[view].uncoded ? 'true' : 'false',
    }),
    ...(source !== 'all' && { source }),
    ...(search.trim() !== '' && { q: search.trim() }),
    ...(triParam(autoFilledFilter) && { autoFilled: triParam(autoFilledFilter)! }),
    ...(triParam(receiptFilter) && { hasReceipt: triParam(receiptFilter)! }),
    limit: PAGE_SIZE,
    offset,
  };
  // Serialised so the effect below depends on the filter VALUES rather than on
  // a fresh object identity every render, which would refetch forever.
  const filterKey = JSON.stringify(filters);

  const load = useCallback(() => {
    setLoading(true);
    api
      .listJournals(JSON.parse(filterKey) as JournalFilters)
      .then((result) => {
        setList(result);
        setListError(null);
        setCursor((current) => Math.min(current, Math.max(result.entries.length - 1, 0)));
      })
      .catch((caught: unknown) =>
        // error.message, not error.code: the API sends a sentence and ApiError
        // now keeps it, falling back to the code when it does not.
        setListError(caught instanceof ApiError ? caught.message : 'Could not load the queue.'),
      )
      .finally(() => setLoading(false));
  }, [filterKey]);

  useEffect(load, [load]);

  const rows: JournalListRow[] = list?.entries ?? [];
  const active = rows[cursor];

  /*
   * Only the rows a batch could act on are asked about: a draft with no lines.
   * Anything else comes back as a skip, and asking costs a rule lookup per row
   * on the server -- fifty of them on the Posted tab, to be told fifty times
   * that a posted entry is not a draft.
   *
   * Serialised into a string for the dependency, for the same reason
   * `filterKey` is: a fresh array every render would refetch forever.
   */
  const offerableKey = rows
    .filter((row) => row.status === 'draft' && row.lineCount === 0)
    .map((row) => row.id)
    .join(',');

  useEffect(() => {
    if (offerableKey === '') {
      setOffers(new Map());
      return;
    }
    let cancelled = false;
    api
      .planBulkCoding(offerableKey.split(','))
      .then((drawn) => {
        if (cancelled) return;
        setOffers(new Map(drawn.entries.map((planned) => [planned.headerId, planned])));
      })
      .catch(() => {
        /*
         * Swallowed, and this is the one place in this file that swallows.
         *
         * A badge and a preview are both decoration over a list that has
         * already loaded. An error banner for them would sit above the rows
         * the operator can still perfectly well read, dismiss and code, and it
         * would say nothing they can act on. The absence of the badge is the
         * failure, and it is the same absence as "no rule matches".
         */
        if (!cancelled) setOffers(new Map());
      });
    return () => {
      cancelled = true;
    };
  }, [offerableKey]);

  // The detail pane follows the cursor. Keyed on the id so that arrowing
  // through the list does not refetch the same entry repeatedly.
  const activeId = active?.id;
  useEffect(() => {
    activeIdRef.current = activeId;
    // Whatever was being looked at belonged to the row we just left. Closed
    // rather than left open over a different entry's facts.
    setViewing(null);
    if (activeId === undefined) {
      setEntry(null);
      setReceipts([]);
      return;
    }
    let cancelled = false;
    api
      .getJournal(activeId)
      .then((result) => {
        if (cancelled) return;
        setEntry(result.entry);
        setEntryError(null);
      })
      .catch((caught: unknown) => {
        if (cancelled) return;
        setEntry(null);
        setEntryError(caught instanceof ApiError ? caught.message : 'Could not load the entry.');
      });
    /*
     * A second request per row, and it is worth the round trip: the receipt is
     * the evidence the triage decision is made on, and a list that appeared
     * only after opening the editor would mean opening the editor to find out
     * whether there was anything to open it for.
     *
     * Cleared before the request rather than after it. Otherwise the previous
     * row's receipts stay on screen while this one loads, and a transaction
     * with no receipt shows the last one that had one -- which reads as an
     * attachment on the wrong entry.
     */
    setReceipts([]);
    setReceiptsError(null);
    api
      .listAttachments(activeId)
      .then((result) => {
        if (cancelled) return;
        setReceipts(result.attachments);
      })
      .catch((caught: unknown) => {
        if (cancelled) return;
        setReceiptsError(
          caught instanceof ApiError ? caught.message : 'Could not list the receipts.',
        );
      });
    // A stale response from a row the operator has already moved off must not
    // overwrite the current one. Arrowing fast makes this reachable.
    return () => {
      cancelled = true;
    };
  }, [activeId]);

  const move = useCallback(
    (delta: number) => {
      setCursor((current) => {
        const next = Math.min(Math.max(current + delta, 0), Math.max(rows.length - 1, 0));
        rowsRef.current[next]?.scrollIntoView({ block: 'nearest' });
        return next;
      });
    },
    [rows.length],
  );

  const toggleSelected = useCallback((id: string) => {
    setSelected((current) => {
      const next = new Set(current);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }, []);

  /*
   * Whether the page is wholly or partly ticked, computed from the rows on
   * screen rather than from the size of the set: the set outlives a filter
   * change, so twelve selected ids from the Uncategorized tab must not read as
   * "all" on a Posted tab showing three.
   */
  const allSelected = rows.length > 0 && rows.every((row) => selected.has(row.id));
  const someSelected = rows.some((row) => selected.has(row.id));

  /*
   * Clears every row ON THIS PAGE, not the whole set. Ticking page one, paging
   * forward and clearing page two would otherwise silently drop page one's
   * selection, and the count in the toolbar would be the only clue.
   */
  function toggleAll() {
    setSelected((current) => {
      const next = new Set(current);
      if (allSelected) for (const row of rows) next.delete(row.id);
      else for (const row of rows) next.add(row.id);
      return next;
    });
  }

  const dismiss = useCallback(
    async (row: JournalListRow) => {
      try {
        await api.dismissJournal(row.id);
        setNotice(`Excluded ${row.counterparty ?? 'entry'} — press u to undo`);
        setSelected((current) => {
          const next = new Set(current);
          next.delete(row.id);
          return next;
        });
        load();
      } catch (caught) {
        setNotice(
          caught instanceof ApiError
            ? // not_a_draft is the real case: somebody else posted or dismissed
              // it in another tab. Saying so beats "something went wrong".
              caught.message
            : 'Could not dismiss that entry.',
        );
      }
    },
    [load],
  );

  const undismiss = useCallback(
    async (row: JournalListRow) => {
      try {
        await api.undismissJournal(row.id);
        setNotice(`Restored ${row.counterparty ?? 'entry'} to the queue`);
        load();
      } catch (caught) {
        setNotice(
          caught instanceof ApiError ? caught.message : 'Could not restore that entry.',
        );
      }
    },
    [load],
  );

  /**
   * Global key handling, skipped while a text field has focus so typing in the
   * search box does not navigate the list.
   */
  useEffect(() => {
    function onKeyDown(event: KeyboardEvent) {
      /*
       * Nothing on the queue is reachable while the overlay is up, and `d` is
       * the reason this is not merely tidy: the row under the cursor is behind
       * the overlay, so a `d` typed while reading a receipt would exclude an
       * entry the operator cannot see, with only the undo notice to say so.
       *
       * The overlay owns Escape itself; it is not routed through here.
       */
      if (viewing !== null) return;
      const target = event.target as HTMLElement | null;
      if (target && (target.tagName === 'INPUT' || target.tagName === 'SELECT')) return;
      if (event.metaKey || event.ctrlKey || event.altKey) return;

      const row = rows[cursor];

      switch (event.key) {
        case 'j':
        case 'ArrowDown':
          event.preventDefault();
          move(1);
          break;
        case 'k':
        case 'ArrowUp':
          event.preventDefault();
          move(-1);
          break;
        case ' ':
          if (!row) break;
          event.preventDefault();
          toggleSelected(row.id);
          break;
        case 'd':
          if (!row || row.status !== 'draft') break;
          event.preventDefault();
          void dismiss(row);
          break;
        case 'u':
          if (!row || row.status !== 'dismissed') break;
          event.preventDefault();
          void undismiss(row);
          break;
        // `e` from prototype/FINDINGS.md, plus Enter because that is what a
        // list of things you open makes people press. The editor exists now,
        // so unlike `a` this key has somewhere real to go.
        case 'e':
        case 'Enter':
          if (!row) break;
          event.preventDefault();
          navigate(`/journals/${row.id}${editorSuffix}`);
          break;
        default:
          break;
      }
    }

    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [rows, cursor, move, toggleSelected, dismiss, undismiss, navigate, viewing]);

  const total = list?.total ?? 0;
  const showingFrom = total === 0 ? 0 : offset + 1;
  const showingTo = offset + rows.length;

  return (
    <section className="triage">
      <header className="row triage-head">
        <h1>Entries</h1>
        <span className="muted">
          {loading ? 'Loading…' : `${total.toLocaleString('en-US')} matching`}
        </span>
      </header>

      {/*
        §17 — CREATING AN ENTRY MOVED OUT OF THIS SCREEN, 2026-09-12.

        A four-field form used to live here, behind a link. It collected the
        date, who it was with, an invoice number and a memo, created the
        header, then sent you to the editor -- because those four were exactly
        the fields the editor could not edit. It was a workaround for the
        editor's field coverage and it read as a preamble to invoicing.

        The operator who commissioned this asked where invoices were supposed
        to be entered, which answered the question about whether it was
        findable. It is a button in the top bar now, and the editor creates the
        entry on its first save.
      */}

      {/*
        Status as TABS, not a dropdown.

        These five are the operator's whole working day and they switch between
        them constantly -- a dropdown hides four of the five behind a click and
        gives no sense of where you are. Source stays a select below: it is a
        narrowing you set once and leave.

        The vocabulary is unchanged and deliberate. `status` in the database is
        three values and covers two of these: a draft with no lines is a bank
        transaction nobody has categorised, and a draft with lines is an entry
        waiting to be posted. Different jobs, so different tabs.
      */}
      <nav className="row tabs" aria-label="Status">
        {(Object.keys(VIEWS) as ViewKey[]).map((key) => (
          <button
            key={key}
            type="button"
            className={`tab${view === key ? ' is-current' : ''}`}
            aria-current={view === key}
            onClick={() => {
              setView(key);
              setOffset(0);
              setCursor(0);
            }}
          >
            {VIEWS[key].label}
          </button>
        ))}
      </nav>

      <div className="row triage-filters">

        {/*
          Source, which used to be invisible.

          Eleven hand-entered invoices sat among 312 Mercury imports with no
          way to tell them apart but a counterparty search. The API has
          supported this filter since Phase 2; nothing had ever exposed it.
        */}
        <label>
          Source{' '}
          <select
            value={source}
            onChange={(event) => {
              setSource(event.target.value as SourceKey);
              setOffset(0);
              setCursor(0);
            }}
          >
            {(Object.keys(SOURCES) as SourceKey[]).map((key) => (
              <option key={key} value={key}>
                {SOURCES[key]}
              </option>
            ))}
          </select>
        </label>

        <label>
          Search{' '}
          <input
            type="search"
            value={search}
            placeholder="counterparty, memo, invoice ref"
            onChange={(event) => {
              setSearch(event.target.value);
              setOffset(0);
              setCursor(0);
            }}
          />
        </label>

        <button
          type="button"
          className="link"
          aria-expanded={advancedOpen}
          aria-controls="advanced-search"
          onClick={() => setAdvancedOpen((open) => !open)}
        >
          Advanced Search
          {activeAdvanced > 0 && ` (${activeAdvanced})`}
        </button>

        {/*
          §16.2 — the bulk actions, offered only when something is selected.

          "accept 12 rule matches" is what `coding_rules` was shaped for: one
          pattern, one account, one line, so the button has exactly one meaning.
          Multi-line templates were deferred precisely to keep it that way.

          Two buttons rather than one with a checkbox. Coding is reversible and
          posting is not, and a modifier on a shared control is how the wrong
          one gets pressed.
        */}
        {/*
          SHOWN ALWAYS, DISABLED WHEN NOTHING IS SELECTED. They used to render
          only once something was selected, and selection was the space bar and
          nothing else -- no checkbox, no hint. The operator who asked for bulk
          coding could not find it on the screen it was built into, and asked
          whether it had been built at all. A control that appears from nowhere
          is a control nobody knows exists.

          `title` carries the reason rather than a disabled button sitting
          there mute.
        */}
        {/*
          SELECT ALL, SCOPED TO THIS PAGE AND NOT TO THE FILTER.

          "Everything matching" would be every uncategorized entry, of which
          the operator can see fifty. A control that acts on rows nobody has
          looked at is one whose effect cannot be checked before it runs, and a
          confirmation listing them all is not a review, it is a wall.

          IN THE TOOLBAR AND NOT IN THE LIST. It was an <li> at the top of the
          <ol> first, which made it a list item: every test counting entries
          counted one too many, and a screen reader would have announced the
          control as one of the entries.

          Indeterminate when only some are ticked, which is the state that says
          the tick was partial rather than off.
        */}
        {/* Only for someone who may edit entries (0069). */}
        {edits && (
          <>
            <label className="selectall">
              <input
                type="checkbox"
                aria-label="Select every entry on this page"
                disabled={rows.length === 0}
                checked={allSelected}
                ref={(element) => {
                  if (element) element.indeterminate = someSelected && !allSelected;
                }}
                onChange={() => toggleAll()}
              />
              Select page
            </label>
            <span className="muted">
              {selected.size > 0 ? `${selected.size} selected` : 'None selected'}
            </span>
            {/*
              ONE BUTTON AND A MENU, the operator's layout (2026-09-13). Auto Post is
              the default because posting what the rules fill is the job this list
              exists for; Auto Draft (code, leave as drafts) and Exclude sit under
              the caret. Both Auto actions still open the confirmation that lists
              every line, so the menu changes where the choice is made, not whether
              the operator sees what will be written.
            */}
            <div className="split-button" ref={bulkMenuRoot}>
              <button
                type="button"
                className="split-main"
                disabled={bulkBusy || selected.size === 0}
                title={selected.size === 0 ? 'Tick some rows first' : undefined}
                onClick={() => void openBulk(true)}
              >
                Auto Post
              </button>
              <button
                type="button"
                className="split-caret"
                aria-haspopup="menu"
                aria-expanded={bulkMenuOpen}
                aria-label="More actions for the selected entries"
                disabled={bulkBusy || selected.size === 0}
                onClick={() => setBulkMenuOpen((open) => !open)}
              >
                ▾
              </button>
              {bulkMenuOpen && (
                <div className="new-entry-menu split-menu" role="menu">
                  <button
                    type="button"
                    role="menuitem"
                    onClick={() => {
                      setBulkMenuOpen(false);
                      void openBulk(false);
                    }}
                  >
                    Auto Draft
                  </button>
                  <button
                    type="button"
                    role="menuitem"
                    onClick={() => {
                      setBulkMenuOpen(false);
                      setConfirmingExclude(true);
                    }}
                  >
                    Exclude
                  </button>
                </div>
              )}
            </div>
          </>
        )}
      </div>

      {advancedOpen && (
        <div
          id="advanced-search"
          className="row triage-advanced"
          role="group"
          aria-label="Advanced Search"
        >
          <TriStateCheck
            label="Auto-Filled"
            value={autoFilledFilter}
            has="only the entries a rule will fill"
            lacks="only the entries no rule will fill"
            onChange={(next) => {
              setAutoFilledFilter(next);
              setOffset(0);
              setCursor(0);
            }}
          />
          <TriStateCheck
            label="Has Receipt"
            value={receiptFilter}
            has="only the entries with a receipt"
            lacks="only the entries with no receipt"
            onChange={(next) => {
              setReceiptFilter(next);
              setOffset(0);
              setCursor(0);
            }}
          />
          {/*
            Stated, because a cycling control's third state is not visible
            until someone happens to click twice. One sentence under the row
            costs less than an operator who never finds it.
          */}
          <span className="muted tri-hint">
            Click once for a tick (has it), again for a cross (does not).
          </span>
        </div>
      )}

      {/*
        Excluding is reversible one entry at a time from the Excluded tab, so a
        batch of them is asked about first. It says how to undo it rather than
        asking whether the operator is sure.
      */}
      {confirmingExclude && (
        <div className="new-account undo-form" role="group" aria-label="Exclude Selected">
          <p>
            Exclude {selected.size} {selected.size === 1 ? 'entry' : 'entries'}? They leave the
            queue and can be restored from the Excluded tab.
          </p>
          <div className="row">
            <button type="button" disabled={bulkBusy} onClick={() => void excludeSelected()}>
              Exclude Them
            </button>
            <button type="button" className="link" onClick={() => setConfirmingExclude(false)}>
              Cancel
            </button>
          </div>
        </div>
      )}

      {/*
        A toast, like the editor's: every notice here is an event that just
        happened ("6 posted", "Excluded 3 entries"), and it fades after five
        seconds rather than standing over the list until the next one.
      */}
      {notice && <Toast message={notice} onDismiss={() => setNotice(null)} />}
      {listError && (
        <p role="alert" className="error">
          {listError}
        </p>
      )}

      <div className="triage-body">
        <ol className="queue" aria-label="Draft entries">
          {rows.map((row, index) => (
            <li
              key={row.id}
              ref={(element) => {
                rowsRef.current[index] = element;
              }}
              className={`queue-row${index === cursor ? ' is-cursor' : ''}${
                selected.has(row.id) ? ' is-selected' : ''
              }${dropRow === row.id ? ' is-dropping' : ''}${
                uploadingRow === row.id ? ' is-busy' : ''
              }`}
              aria-current={index === cursor}
              onClick={() => setCursor(index)}
              onDoubleClick={() => navigate(`/journals/${row.id}${editorSuffix}`)}
              // A file dragged over the row: both handlers must preventDefault
              // or the browser opens the file and the page is lost.
              onDragOver={(event) => {
                if (!Array.from(event.dataTransfer?.types ?? []).includes('Files')) return;
                event.preventDefault();
                if (uploadingRow === null) setDropRow(row.id);
              }}
              onDragLeave={(event) => {
                // Moving onto a child of the same row is not leaving it.
                if (event.currentTarget.contains(event.relatedTarget as Node | null)) return;
                setDropRow((current) => (current === row.id ? null : current));
              }}
              onDrop={(event) => {
                const files = Array.from(event.dataTransfer?.files ?? []);
                if (files.length === 0) return;
                event.preventDefault();
                setDropRow(null);
                if (uploadingRow === null) void dropReceipt(row, files);
              }}
            >
              {/*
                THE CHECKBOX IS THE POINT OF THIS ROW'S REDESIGN. Selection was
                the space bar and nothing visible, so the bulk actions might as
                well not have existed.

                stopPropagation, because the row's own onClick moves the cursor
                and a tick should not also drag the detail pane onto a row the
                operator only meant to mark.
              */}
{edits && (
                <input
                  type="checkbox"
                  className="queue-tick"
                  checked={selected.has(row.id)}
                  aria-label={`Select ${row.counterparty ?? 'entry'} on ${row.entryDate}`}
                  onClick={(event) => event.stopPropagation()}
                  onChange={() => toggleSelected(row.id)}
                />
              )}
              <span className="queue-date">{row.entryDate}</span>
              <span className="queue-party">{row.counterparty ?? <em>no counterparty</em>}</span>
              <span className="queue-amount">{amountLabel(row)}</span>
              {/*
                No badge for a transaction that has not been coded. Every one
                in production is in that state, so a tag saying so
                would appear on every row and tell an operator nothing; the
                absence of a badge IS "not coded yet". Only the two states
                worth reacting to are marked.
              */}
              {/*
                §7 and §9.7 — the two things a row can say about itself before
                anybody opens it.

                AUTO-FILLED means a rule matches and the recommended lines are
                sitting in the detail pane already. It is not "this is coded":
                nothing has been written, and the row is still uncategorized.
                It is the answer to "which of these 239 can I just accept".

                THE PAPERCLIP is evidence attached. Both are absences most of
                the time, which is what keeps them worth looking at.
              */}
              <span className="queue-marks">
                {offers.has(row.id) && (
                  <span className="tag tag-offer" title="A rule matches this counterparty">
                    Auto-Filled
                  </span>
                )}
                {row.receiptCount > 0 && (
                  <span
                    className="tag tag-receipt"
                    title={`${row.receiptCount} receipt${row.receiptCount === 1 ? '' : 's'}`}
                  >
                    {/* The count rides along: two receipts and one are a
                        different thing when you are looking for a missing
                        half of a pair. */}
                    {row.receiptCount === 1 ? 'Receipt' : `Receipts ${row.receiptCount}`}
                  </span>
                )}
                {/*
                  THE STATUS BADGE LIVES IN THIS SPAN, beside the others.

                  It used to be a sibling `.queue-state` span, and since both
                  sat in grid column 3 the grid stacked them -- Receipt on one
                  line, posted on the next. Two grid items in one column cannot
                  share a line, so the fix is here rather than in the
                  stylesheet.

                  NO "ready to post" badge.

                    It appeared on every correctly coded row, which is most of
                    them once the queue is worked -- a badge that says "this
                    one is fine" on the majority is decoration, and it competed
                    with the one that is not fine. The absence of a red badge
                    now means ready.

                    "posted" stays, because under Status: All it is the only
                    thing distinguishing a finished entry from a draft.
                  */}
                {row.lineCount > 0 &&
                  (row.status === 'posted' ? (
                    <span className="tag tag-posted">posted</span>
                  ) : row.balanced ? null : (
                    <span className="tag tag-unbalanced">incomplete</span>
                  ))}
              </span>
            </li>
          ))}
          {!loading && rows.length === 0 && (
            <li className="queue-empty muted">Nothing matches these filters.</li>
          )}
        </ol>

        <aside className="detail-pane" aria-label="Entry detail">
          {entryError && (
            <p role="alert" className="error">
              {entryError}
            </p>
          )}
          {entry && (
            <>
              <h2>{entry.counterparty ?? 'No counterparty'}</h2>
              <dl className="facts">
                <div className="fact">
                  <dt>Entry date</dt>
                  <dd>{entry.entryDate}</dd>
                </div>
                <div className="fact">
                  <dt>Bank amount</dt>
                  <dd>{bankAmountLabel(entry.bankAmountCents)}</dd>
                </div>
                <div className="fact">
                  <dt>Status</dt>
                  <dd>{statusLabel({ status: entry.status, lineCount: entry.lines.length })}</dd>
                </div>
                <div className="fact">
                  <dt>Source</dt>
                  <dd>{entry.source === 'manual' ? 'Manual Entry' : 'Bank Feed'}</dd>
                </div>
                {/* §7 — who it is with, as the operator classified it. Not the bank's text. */}
                <div className="fact">
                  <dt>Party</dt>
                  <dd>{entry.partyName ?? '—'}</dd>
                </div>
                {/* Only when there is one: a bank row never has an invoice number. */}
                {entry.invoiceReference && (
                  <div className="fact">
                    <dt>Invoice Number</dt>
                    <dd>{entry.invoiceReference}</dd>
                  </div>
                )}
                {/* §9.1 — how the money moved. See EditorPage for why this is
                    not an ACH-versus-check answer, and why it replaced the
                    version. Null is "not captured", not "no rail". */}
                <div className="fact">
                  <dt>Method</dt>
                  <dd title={entry.mercuryKind ?? undefined}>{kindLabel(entry.mercuryKind)}</dd>
                </div>
                <div className="fact">
                  {/*
                    "Bank Categorization", not "Category". Mercury's guess and
                    the operator's accounts are different things and sharing a
                    word made a row read "Category: Software" while sitting
                    under Uncategorized.
                  */}
                  <dt>Bank Categorization</dt>
                  <dd>{entry.mercuryCategory ?? '—'}</dd>
                </div>
                <div className="fact">
                  <dt>Memo</dt>
                  <dd>{entry.memo ?? '—'}</dd>
                </div>
                {/*
                  §9.1 and migration 0026 — the bank's own text, shown ONLY
                  when it is not the same sentence as the memo above.

                  On a header nobody has retyped the two are identical, and a
                  pane that printed both would say everything twice on nearly
                  every row. It appears exactly when the operator has written
                  their own memo, which is the moment the bank's wording stops
                  being visible anywhere else.
                */}
                {entry.bankMemo !== null && entry.bankMemo !== entry.memo && (
                  <div className="fact">
                    <dt>Bank Memo</dt>
                    <dd>{entry.bankMemo}</dd>
                  </div>
                )}

              </dl>

              {/*
                §7 — the receipts, between the facts and the coding.

                Placed here rather than at the foot of the pane because it is
                evidence for the decision above it, not a footnote to the lines
                below: "is this worth coding" is answered by looking at what
                the transaction was, and for anything ambiguous that means the
                receipt.

                NO EMPTY STATE, for the reason the editor's list has none. Most
                headers have no receipt and never will, and a line saying so
                on every one of them would be a reproach on the ordinary case.
                Absence of the section IS "nothing attached".
              */}
              {receiptsError && (
                <p role="alert" className="error">
                  {receiptsError}
                </p>
              )}
              {receipts.length > 0 && (
                <ul className="receipt-list" aria-label="Receipts">
                  {receipts.map((receipt) => (
                    <li key={receipt.id}>
                      <button
                        type="button"
                        className="link"
                        onClick={() => setViewing(receipt)}
                      >
                        {receipt.originalFilename}
                      </button>
                    </li>
                  ))}
                </ul>
              )}

              {/*
                §7 and §16.2 — filing a receipt without leaving the list.

                THIS IS WHAT MAKES BULK POSTING USABLE. Bulk is for the
                transactions that need no evidence; the ones that do had to be
                opened one at a time in the editor purely to drop a file on
                them, which is the round trip bulk exists to avoid. Now the
                operator arrows down the list, files the two receipts that
                matter, and posts the rest in one action.

                OFFERED ON EVERY ENTRY, posted and closed included. Migration
                0018 permits it -- an INSERT into `attachments` does not change
                the entry -- and the ordinary case is a PDF arriving by email a
                week after the transaction cleared. The editor hides its zone
                on an entry inside a closed period, where a full-width dashed
                rectangle would read as an editable record on a read-only
                page. A single line in this pane makes no such claim, so this
                is where a late receipt for a closed year gets filed.
              */}
              {edits && (
                <ReceiptDropzone
                  compact
                  onResolveTarget={() => entry.id}
                  onAttached={(attachments, headerId) => {
                    setNotice(`Attached ${describeAttached(attachments)}`);
                    /*
                     * The list is re-read whichever entry it landed on, exactly
                     * as `dropReceipt` does for a drop straight onto a row. The
                     * row's Receipt badge is drawn from `receiptCount`, which
                     * comes from the list and not from `receipts` -- so without
                     * this the file attached, the pane listed it, and the row
                     * the operator was looking at went on showing no badge until
                     * something else happened to reload. The pane and the row
                     * disagreed about the same entry.
                     */
                    load();
                    if (headerId !== activeIdRef.current) return;
                    setReceipts((current) => [...current, ...attachments]);
                  }}
                  onError={setReceiptsError}
                />
              )}

              {/*
                §9.7 — THE RECOMMENDATION, SHOWN BEFORE ANYONE COMMITS TO IT.

                This is the same plan the bulk confirmation renders, drawn for
                the whole page at once. Until now the only way to see what a
                rule would write was to select rows and press a button, which
                put the first sight of the lines inside the dialog that asks
                whether to write them. Reading and deciding were the same
                keystroke.

                NOTHING HAS HAPPENED. The wording has to carry that, because
                the table looks exactly like the one below it that shows lines
                already saved -- deliberately, since they are the same lines,
                but one is a proposal and one is a record. The heading and the
                provenance line are what separate them, and the confirmation
                still stands between this and any write.
              */}
              {entry.lines.length === 0 && offers.has(entry.id) && (
                <div className="offer">
                  <p className="offer-head">
                    <strong>Recommended categorization</strong>{' '}
                    <span className="muted">
                      from the rule matching “{offers.get(entry.id)!.rulePattern}”
                    </span>
                  </p>
                  <table className="lines">
                    <tbody>
                      {offers.get(entry.id)!.lines.map((line, index) => (
                        <tr key={`${entry.id}-offer-${index}`}>
                          <td>
                            {line.accountCode} {line.accountName}
                          </td>
                          <td className="num">
                            {line.debitCents > 0 ? formatCents(line.debitCents) : ''}
                          </td>
                          <td className="num">
                            {line.creditCents > 0 ? formatCents(line.creditCents) : ''}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                  <p className="muted">
                    Nothing is written yet. Tick this entry and use Auto Draft, or
                    open it in the editor to change it first.
                  </p>
                </div>
              )}

              {entry.lines.length === 0 ? (
                <p className="muted">
                  {offers.has(entry.id)
                    ? 'No accounts are assigned yet, so this cannot be posted. The recommendation above is a proposal, not a categorization.'
                    : 'Not categorized yet — no accounts have been assigned, so this cannot be posted. Assigning them is the editor, which is the next screen.'}
                </p>
              ) : (
                <table className="lines">
                  <thead>
                    <tr>
                      <th>Account</th>
                      <th className="num">Debit</th>
                      <th className="num">Credit</th>
                    </tr>
                  </thead>
                  <tbody>
                    {entry.lines.map((line) => (
                      <tr key={line.id}>
                        <td>
                          {line.accountCode} {line.accountName}
                        </td>
                        <td className="num">
                          {line.debitCents > 0 ? formatCents(line.debitCents) : ''}
                        </td>
                        <td className="num">
                          {line.creditCents > 0 ? formatCents(line.creditCents) : ''}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </>
          )}
          {!entry && !entryError && <p className="muted">Select an entry.</p>}
        </aside>
      </div>

      {/*
        Rendered at the page level rather than inside the pane, so the panel is
        not clipped by the pane's own box and does not inherit its width. It is
        keyed on the receipt id: switching receipts must remount, or the frame
        keeps the previous document while the new signed URL is fetched.
      */}
      {viewing !== null && (
        <ReceiptOverlay
          key={viewing.id}
          attachment={viewing}
          onClose={() => setViewing(null)}
        />
      )}

      <div className="row triage-foot">
        <span className="muted">
          {total === 0 ? 'No entries' : `Showing ${showingFrom}–${showingTo} of ${total}`}
        </span>
        <span>
          <button
            type="button"
            className="link"
            disabled={offset === 0}
            onClick={() => {
              setOffset(Math.max(offset - PAGE_SIZE, 0));
              setCursor(0);
            }}
          >
            Previous
          </button>{' '}
          <button
            type="button"
            className="link"
            disabled={showingTo >= total}
            onClick={() => {
              setOffset(offset + PAGE_SIZE);
              setCursor(0);
            }}
          >
            Next
          </button>
        </span>
        {/*
          Behind a button now.

          The list was permanent, five items wide, at the bottom of every
          screen -- and it is read once, in the first week, and then never
          again. `a` is absent on purpose: there is no suggestion engine, so
          there is nothing to accept.

          A disclosure rather than a hover tooltip, for the reason the §9.3
          note in the editor is one too: hover has no touch equivalent and no
          keyboard one.
        */}
        <span className="keys">
          <button
            type="button"
            className="info-dot"
            aria-expanded={keysOpen}
            aria-controls="queue-shortcuts"
            aria-label={keysOpen ? 'Hide keyboard shortcuts' : 'Keyboard shortcuts'}
            onClick={() => setKeysOpen((open) => !open)}
          >
            ⌨
          </button>
          {keysOpen && (
            <span className="muted" id="queue-shortcuts">
              {' '}j/k move · e edit · space select · d exclude · u restore
            </span>
          )}
        </span>
      </div>
      {/*
        §16.2 — the bulk confirmation. The editor no longer confirms a single
        post (a posted entry is editable until the books close, migration
        0027); a batch still does, because a wrong rule is multiplied by it.

        It shows every entry's lines, so the operator answers against what will
        be written rather than against a number. For CODING it is arguably more
        than is needed, since coding is reversible -- but a second dialog that
        showed less would drift from this one, and "which of the two am I
        looking at" is a worse problem than one extra scroll.
      */}
      {plan && (
        <ConfirmPost
          // A duplicate the operator chose to exclude is not coded, so it is not listed.
          entries={plan.entries
            .filter((entry) => !(excludeDuplicates && isDuplicate(entry)))
            .map((entry) => ({
            headerId: entry.headerId,
            entryDate: entry.entryDate,
            counterparty: entry.counterparty,
            hasReceipt: entry.hasReceipt,
            receiptNames: entry.receiptNames,
            memo: Object.hasOwn(memoEdits, entry.headerId) ? memoEdits[entry.headerId] : entry.memo,
            lines: entry.lines,
          }))}
          /*
           * The draft other sides, in the order the server will reach them:
           * when both sides of one transfer are selected, the first is coded
           * and excludes the second, so the second is not listed as a
           * counterpart to exclude again.
           */
          transfers={plan.entries.flatMap((entry, index) => {
            const other = entry.counterpart;
            if (!other || other.status !== 'draft') return [];
            if (plan.entries.slice(0, index).some((earlier) => earlier.headerId === other.headerId)) {
              return [];
            }
            return [
              {
                headerId: other.headerId,
                label: `${other.entryDate} ${other.counterparty ?? 'No counterparty'} on ${other.accountCode} ${other.accountName}`,
              },
            ];
          })}
          excludeTransfers={excludeTransfers}
          onExcludeTransfersChange={setExcludeTransfers}
          duplicates={plan.entries.filter(isDuplicate).map((entry) => ({
            headerId: entry.headerId,
            label: `${entry.entryDate} ${entry.counterparty ?? 'No counterparty'}, already posted on ${entry.counterpart?.accountCode} ${entry.counterpart?.accountName}`,
          }))}
          excludeDuplicates={excludeDuplicates}
          onExcludeDuplicatesChange={setExcludeDuplicates}
          excluded={droppedFromPlan}
          onExcludeChange={(headerId, drop) =>
            setDroppedFromPlan((current) =>
              drop
                ? current.includes(headerId)
                  ? current
                  : [...current, headerId]
                : current.filter((candidate) => candidate !== headerId),
            )
          }
          onMemoChange={(headerId, memo) =>
            setMemoEdits((current) => ({ ...current, [headerId]: memo }))
          }
          busy={bulkBusy}
          post={planPosts}
          note={
            plan.skipped.length > 0
              ? `${plan.skipped.length} of the selected entries will be left alone.`
              : undefined
          }
          onConfirm={() => void runBulk()}
          onCancel={() => setPlan(null)}
        />
      )}
    </section>
  );
}
